/**
 * components/QuestionCard.js — Phase B shared question surface.
 *
 * Renders ONE question row from `user_questions` with inline actions.
 * Used by:
 *   • Dashboard “Questions worth sitting with” tile (top 1–3 open rows)
 *   • /reflections → Prompts tab (full open list + history)
 *
 * Actions supported:
 *   • answer   → opens an inline textarea; POST /api/questions/{id}/answer
 *   • snooze   → default 14 days; POST /api/questions/{id}/snooze
 *   • dismiss  → POST /api/questions/{id}/dismiss
 *   • cta_action starting with `open:` → in-app link (e.g. Settings)
 *
 * Design principles (calm, non-judgemental — per design guidelines):
 *   • subtle brand chrome; no aggressive colour walls
 *   • gold uppercase source label (rule / gap / reflection)
 *   • primary action button only when useful; secondary link for dismiss/snooze
 *   • answered state shows the user's answer in a muted card
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  MessageCircleQuestion, Compass, Sparkles, MoreHorizontal, Clock, X, ExternalLink, Check, Pencil,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANSWER_SOFT_CAP = 280;

function sourceMeta(source) {
  if (source === 'gap') {
    return { label: 'Life profile', Icon: Compass };
  }
  if (source === 'reflection') {
    return { label: 'Reflection', Icon: MessageCircleQuestion };
  }
  return { label: 'Money signal', Icon: Sparkles };
}

export function QuestionCard({
  question,
  token,
  onChanged,     // called after any successful action
  compact = false,
}) {
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);

  if (!question) return null;

  const { Icon, label } = sourceMeta(question.source);
  const isOpen      = question.status === 'open';
  const isAnswered  = question.status === 'answered';
  const isDismissed = question.status === 'dismissed';
  const isSnoozed   = question.status === 'snoozed';

  const authHeaders = { Authorization: `Bearer ${token}` };

  async function submitAnswer() {
    const text = (answer || '').trim();
    if (!text) {
      toast.error('Add a few words to save your answer.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/questions/${question.id}/answer`,
        { text },
        { headers: authHeaders, timeout: 12000 },
      );
      toast.success('Saved to your journal.');
      setAnswering(false);
      setAnswer('');
      onChanged?.();
    } catch (e) {
      toast.error('Could not save just now. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  async function dismiss() {
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/questions/${question.id}/dismiss`,
        {},
        { headers: authHeaders, timeout: 8000 },
      );
      toast.success('Cleared.');
      onChanged?.();
    } catch (e) {
      toast.error('Could not dismiss just now.');
    } finally {
      setBusy(false);
    }
  }

  async function snooze(days = 14) {
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/questions/${question.id}/snooze`,
        { days },
        { headers: authHeaders, timeout: 8000 },
      );
      toast.success(`Snoozed for ${days} days.`);
      onChanged?.();
    } catch (e) {
      toast.error('Could not snooze just now.');
    } finally {
      setBusy(false);
    }
  }

  const cta = question.cta_action || '';

  return (
    <Card
      className={`rounded-2xl border-border/70 ${compact ? '' : 'transition-shadow hover:shadow-sm'}`}
      data-testid={`question-card-${question.id}`}
    >
      <CardContent className={`${compact ? 'p-4' : 'p-5'} space-y-3`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-3.5 w-3.5 text-gold shrink-0" />
            <span className="text-[11px] uppercase tracking-[2px] text-gold whitespace-nowrap">
              {label}
            </span>
          </div>
          {isOpen && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground"
                  disabled={busy}
                  data-testid={`question-menu-${question.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                <DropdownMenuItem onClick={() => snooze(14)} data-testid={`question-snooze-14-${question.id}`}>
                  <Clock className="h-3.5 w-3.5 mr-2" /> Snooze 2 weeks
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => snooze(30)} data-testid={`question-snooze-30-${question.id}`}>
                  <Clock className="h-3.5 w-3.5 mr-2" /> Snooze a month
                </DropdownMenuItem>
                <DropdownMenuItem onClick={dismiss} data-testid={`question-dismiss-${question.id}`}>
                  <X className="h-3.5 w-3.5 mr-2" /> Dismiss
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="font-display text-base sm:text-lg leading-snug" data-testid={`question-text-${question.id}`}>
          {question.question_text}
        </div>
        {question.subtext && (
          <div className="text-xs text-muted-foreground leading-relaxed">
            {question.subtext}
          </div>
        )}

        {/* Answered state — show the saved answer, no action buttons. */}
        {isAnswered && (
          <div className="rounded-xl bg-muted/40 border border-border/50 p-3" data-testid={`question-answer-${question.id}`}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[2px] text-emerald-700 dark:text-emerald-400 mb-1">
              <Check className="h-3 w-3" /> Answered
            </div>
            <div className="text-sm whitespace-pre-wrap">{question.answer_text}</div>
          </div>
        )}

        {isDismissed && (
          <div className="text-[11px] uppercase tracking-[2px] text-muted-foreground">Dismissed</div>
        )}
        {isSnoozed && (
          <div className="text-[11px] uppercase tracking-[2px] text-muted-foreground">
            Snoozed{question.snoozed_until ? ` until ${new Date(question.snoozed_until).toLocaleDateString()}` : ''}
          </div>
        )}

        {/* Open state — either an inline answer sheet or a Reflect button. */}
        {isOpen && (
          answering ? (
            <div className="space-y-2">
              <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="A few words, or a sentence — whatever feels honest."
                className="rounded-xl min-h-[80px] text-sm"
                maxLength={ANSWER_SOFT_CAP * 2}
                data-testid={`question-answer-input-${question.id}`}
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="rounded-xl"
                  onClick={submitAnswer}
                  disabled={busy || !answer.trim()}
                  data-testid={`question-answer-save-${question.id}`}
                >
                  Save to journal
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-xl text-muted-foreground"
                  onClick={() => { setAnswering(false); setAnswer(''); }}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <span className="text-[11px] text-muted-foreground ml-auto">
                  {answer.length}/{ANSWER_SOFT_CAP}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {cta.startsWith('open:') ? (
                <Button
                  asChild
                  size="sm"
                  className="rounded-xl"
                  data-testid={`question-cta-open-${question.id}`}
                >
                  <a href={cta.slice('open:'.length)}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Fill this in
                  </a>
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setAnswering(true)}
                  data-testid={`question-open-answer-${question.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Reflect on this
                </Button>
              )}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}

export default QuestionCard;
